const toNum = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const round2 = (value) => Number(toNum(value).toFixed(2));

/**
 * Same formula as ProductPriceCalculator.calculateFinalPrice
 * (product_price_details.dart).
 */
export function calculateProductPrice(inputs = {}) {
  const {
    metalRate = 0,
    purity = 0,
    inputPurity = 0,
    weight = 0,
    productDiscountPercentage = 0,
    makingCharges = 0,
    makingChargesPercentage = 0,
    makingchargesType = "",
    makingDiscountPercentage = 0,
    gstMakingPercentage = 0,
    taxPercentage = 0,
    deliveryCharges = 0,
    deliveryDiscountPercentage = 0,
    gstDeliveryPercentage = 0,
    packingCharges = 0,
    packingDiscountPercentage = 0,
    gstPackingPercentage = 0,
    hallmarkcharges = 0,
    othercharges = 0,
  } = inputs;

  const metal = toNum(metalRate);
  const bullionPurity = toNum(purity);
  const productPurity = toNum(inputPurity);
  const productWeight = toNum(weight);

  const productRate =
    bullionPurity === 0 ? 0 : (metal / bullionPurity) * productPurity;
  const productCost = productWeight * productRate;

  const productDiscount = toNum(productDiscountPercentage);
  const discountedProductCost =
    productCost - (productCost * productDiscount) / 100;

  const makingType = String(makingchargesType || "").toLowerCase();
  const rawMakingCharges =
    makingType === "percentage" || makingType === "percent"
      ? (productCost * toNum(makingChargesPercentage)) / 100
      : toNum(makingCharges);

  const makingDiscount = toNum(makingDiscountPercentage);
  const discountedMakingCharges =
    rawMakingCharges - (rawMakingCharges * makingDiscount) / 100;
  const gstMaking = toNum(gstMakingPercentage);
  const gstOnMaking = (discountedMakingCharges * gstMaking) / 100;
  const finalMakingCharges = discountedMakingCharges + gstOnMaking;

  const delivery = toNum(deliveryCharges);
  const deliveryDiscount = toNum(deliveryDiscountPercentage);
  const discountedDeliveryCharges =
    delivery - (delivery * deliveryDiscount) / 100;
  const gstDelivery = toNum(gstDeliveryPercentage);
  const gstOnDelivery = (discountedDeliveryCharges * gstDelivery) / 100;
  const finalDeliveryCharges = discountedDeliveryCharges + gstOnDelivery;

  const packing = toNum(packingCharges);
  const packingDiscount = toNum(packingDiscountPercentage);
  const discountedPackingCharges = packing - (packing * packingDiscount) / 100;
  const gstPacking = toNum(gstPackingPercentage);
  const gstOnPacking = (discountedPackingCharges * gstPacking) / 100;
  const finalPackingCharges = discountedPackingCharges + gstOnPacking;

  let taxableAmount = discountedProductCost;
  if (gstMaking === 0) taxableAmount += discountedMakingCharges;
  if (gstDelivery === 0) taxableAmount += discountedDeliveryCharges;
  if (gstPacking === 0) taxableAmount += discountedPackingCharges;

  const tax = toNum(taxPercentage);
  const taxAmount = (taxableAmount * tax) / 100;
  const hallmark = toNum(hallmarkcharges);
  const other = toNum(othercharges);

  const finalPrice = round2(
    taxableAmount +
      hallmark +
      other +
      taxAmount +
      (gstMaking > 0 ? finalMakingCharges : 0) +
      (gstDelivery > 0 ? finalDeliveryCharges : 0) +
      (gstPacking > 0 ? finalPackingCharges : 0),
  );

  return {
    productRate: round2(productRate),
    productCost: round2(productCost),
    discountedProductCost: round2(discountedProductCost),
    productDiscountPercentage: productDiscount,
    taxPercentage: tax,
    taxAmount: round2(taxAmount),
    finalProductPriceWithTax: round2(taxableAmount + taxAmount),
    makingcharges: round2(rawMakingCharges),
    makingDiscountPercentage: makingDiscount,
    discountonmaking: round2(discountedMakingCharges),
    gstMakingPercentage: gstMaking,
    gstonmakingcharges: round2(gstOnMaking),
    finalmakingcharges: round2(finalMakingCharges),
    deliverycharges: round2(delivery),
    deliveryDiscountPercentage: deliveryDiscount,
    discountondelivery: round2(discountedDeliveryCharges),
    gstDeliveryPercentage: gstDelivery,
    gstondeliverycharges: round2(gstOnDelivery),
    finalDeliveryCharges: round2(finalDeliveryCharges),
    packingcharges: round2(packing),
    packingDiscountPercentage: packingDiscount,
    discountonpacking: round2(discountedPackingCharges),
    gstPackingPercentage: gstPacking,
    gstonpackingcharges: round2(gstOnPacking),
    finalPackingCharges: round2(finalPackingCharges),
    hallmarkcharges: round2(hallmark),
    othercharges: round2(other),
    finalPrice,
  };
}

export function calculateProductPriceFromProduct(product, metalRate) {
  const charges = product?.productCharges || {};

  return calculateProductPrice({
    metalRate,
    purity: product?.bullion?.purity ?? product?.purity,
    inputPurity: product?.purity ?? product?.bullion?.purity,
    weight: product?.metalWeight ?? product?.metal_weight,
    productDiscountPercentage:
      charges.productDiscountPercentage ?? product?.discount ?? 0,
    makingCharges: charges.makingCharges,
    makingChargesPercentage: charges.makingChargesPercentage,
    makingchargesType: charges.makingchargesType,
    makingDiscountPercentage: charges.makingDiscountPercentage,
    gstMakingPercentage: charges.gstMakingPercentage,
    taxPercentage: charges.taxPercentage ?? product?.taxPercentage ?? 0,
    deliveryCharges: charges.deliveryCharges,
    deliveryDiscountPercentage: charges.deliveryDiscountPercentage,
    gstDeliveryPercentage: charges.gstDeliveryPercentage,
    packingCharges: charges.packingCharges,
    packingDiscountPercentage: charges.packingDiscountPercentage,
    gstPackingPercentage: charges.gstPackingPercentage,
    hallmarkcharges: charges.hallmarkCharges ?? charges.hallmarkcharges,
    othercharges: charges.otherCharges ?? charges.othercharges,
  });
}
